import { Link, useLocation, useNavigate } from '@remix-run/react';
import { useCallback, FC, SyntheticEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNumFormatter } from '~/utils/format/num-formatter';
import VideoIcon from 'icon/play_fill.svg';
import HeartIcon from 'icon/heart.svg';
import { ExtractAtomValue, useAtomValue } from 'jotai';
import { ClickPosition } from '~/utils/click-position';
import { usePageActive } from '~/utils/active/use-page-active';
import useReflowClick from '~/utils/use-reflow-click';
import { useViewTracking } from '~/utils/use-view-tracking';
import { Article, DETAIL_POST_MODAL_NAME, EArticleClass, LegacyArticleUtil } from 'shared/models';
import { ModuleType } from '~/utils/module-type';
import { CardIndex } from '../card-index';
import { $InstalledRelatedApp, $UserAgent } from '@lemon8/web-app-shared/atom';
import { useSEOHref } from '~/utils/build-seo-url';

import './style.scss';

interface SourceArticleCardProps {
  article: Article;
  index: number;
}

export const SourceArticleCard: FC<SourceArticleCardProps> = ({ article, index }) => {
  const {
    articleClass,
    articleLikes,
    shortContent,
    title,
    largeImage,
    groupId,
    author,
    imageList,
    ocrTextArr = [],
  } = article;

  const articleLink = useSEOHref(LegacyArticleUtil.getArticlePath(article));

  const authorLink = useSEOHref(LegacyArticleUtil.getAuthorPath(article));

  const imageDetail = useMemo(() => {
    if (articleClass === EArticleClass.GALLERY) {
      return imageList[0];
    }
    return largeImage;
  }, [largeImage, imageList]);

  const { width, height } = imageDetail;
  const { t } = useTranslation();
  const formatNum = useNumFormatter();

  const active = usePageActive();
  const navigate = useNavigate();
  const location = useLocation();

  const { isBot } = useAtomValue($UserAgent);

  const isOldUser = !(['pending', null] as ExtractAtomValue<typeof $InstalledRelatedApp>[]).includes(
    useAtomValue($InstalledRelatedApp),
  );

  const handleArticleClick = useReflowClick(
    {
      clickEvent: (_, bundle) => ({
        ...bundle,
        current_page_module: ModuleType.experience_post,
        current_position: ClickPosition.related_post,
        group_id: groupId,
        page_click_id: groupId,
        rank: index,
      }),
      finallyBehavior: (event) => {
        event?.preventDefault();
        if (!isOldUser) {
          navigate(
            {
              pathname: '.',
              search: location.search, // must keep search otherwise search will disappear
            },
            {
              state: { enterName: DETAIL_POST_MODAL_NAME, gid: groupId, authorId: author.userId },
            },
          ); // push state but not change url
        } else {
          active({
            schemaOption: {
              pageType: articleClass === EArticleClass.VIDEO ? 'video' : 'article',
              groupId,
              authorId: author.userId,
            },
            ampExtra: {
              enter_position: ClickPosition.related_post,
            },
          });
        }
      },
    },
    [active, groupId, articleClass, author],
  );

  const sourceArticleCardRef = useViewTracking<HTMLDivElement>({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_page_module: ModuleType.experience_post,
        current_position: ClickPosition.related_post,
        group_id: groupId,
        rank: index,
      });

      window.Tea('wap_related_post_show', {
        current_page_module: ModuleType.experience_post,
        current_position: ClickPosition.related_post,
        group_id: groupId,
        media_id: author.userId,
        article_class: articleClass,
        rank: index,
      });
    }, []),
  });

  const imgStyle = useMemo(() => {
    return {
      width: '100%',
      height: `${Math.min((((height / width) * 156) / 208) * 100, 100)}%`,
    };
  }, [height, width]);

  return (
    <Link className="source-article-card" onClick={handleArticleClick} draggable={false} to={articleLink}>
      <div className="source-article-card-cover" ref={sourceArticleCardRef}>
        <div className="source-article-card-cover-image">
          <img src={imageDetail.url} style={imgStyle} alt={t('own_picture').replace('%s', title || shortContent)} />
        </div>
        <div className="source-article-card-cover-bg" />
        <CardIndex index={index} />
        <div className="source-article-card-cover-title">{title || shortContent || ''}</div>
        {articleClass === EArticleClass.VIDEO && (
          <div className="source-article-card-cover-video-badge">
            <VideoIcon />
          </div>
        )}
        <object>
          <Link
            to={authorLink}
            className="source-article-card-cover-desc"
            onClick={(e: SyntheticEvent) => e.preventDefault()}
          >
            <img
              loading="lazy"
              className="author-avatar"
              src={author.avatar}
              onContextMenu={(e: SyntheticEvent) => e.preventDefault()}
            />
            <span className="author-name">{author.nickName}</span>
            <div className="likes-count">
              <HeartIcon />
              <span>{formatNum(articleLikes)}</span>
            </div>
          </Link>
        </object>
      </div>
      {isBot && ocrTextArr.length > 0 && <div className="source-article-card-ocr">{ocrTextArr[0]}</div>}
    </Link>
  );
};
